import type { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { Tooltip } from '~/components/Tooltip';

import { LabeledTextarea } from './LabeledTextarea';

interface ReportReasonFieldsProps {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  reportToBeehiiv: boolean;
  onReportToBeehiivChange: (reportToBeehiiv: boolean) => void;
  disabled?: boolean;
  showActions?: boolean;
  blockUser?: boolean;
  onBlockUserChange?: (blockUser: boolean) => void;
}

export const ReportReasonFields: FC<ReportReasonFieldsProps> = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  reportToBeehiiv,
  onReportToBeehiivChange,
  disabled = false,
  showActions = false,
  blockUser = false,
  onBlockUserChange,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mt-4 flex flex-col gap-3">
      <LabeledTextarea
        id={id}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={2000}
        disabled={disabled}
        required
      />
      {showActions && onBlockUserChange ? (
        <label className="flex items-start gap-2 text-sm text-black">
          <input
            type="checkbox"
            checked={blockUser}
            onChange={(event) => onBlockUserChange?.(event.target.checked)}
            disabled={disabled}
            className="mt-0.5 size-4 rounded border-black/20 text-black focus:ring-black/20"
          />
          <span>{t('community.report.block_and_hide_option', 'Block user and hide content')}</span>
        </label>
      ) : null}
      <label className="flex items-start gap-2 text-sm text-black">
        <input
          type="checkbox"
          checked={reportToBeehiiv}
          onChange={(event) => onReportToBeehiivChange(event.target.checked)}
          disabled={disabled}
          className="mt-0.5 size-4 rounded border-black/20 text-black focus:ring-black/20"
        />
        <span>{t('community.report.reported_before_option', 'I have reported this multiple times before')}</span>
      </label>
      {reportToBeehiiv ? (
        <div className="rounded-xl bg-black/5 px-3 py-3 text-sm text-black/70">
          {t(
            'community.report.escalate_prompt',
            "Reported before and it wasn't addressed? You can report this content to beehiiv.",
          )}{' '}
          <Tooltip
            interactive
            className="inline-flex align-text-bottom"
            content={
              <Trans
                i18nKey="community.report.beehiiv_tooltip"
                components={{
                  touLink: (
                    // biome-ignore lint/a11y/useAnchorContent: <Trans> injects the localized link text at runtime.
                    <a
                      href="https://www.beehiiv.com/tou"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-1 hover:opacity-80"
                    />
                  ),
                }}
              />
            }
          >
            <button
              type="button"
              aria-label={t('community.report.beehiiv_tooltip_label', 'What is beehiiv?')}
              className="flex text-black/50 hover:text-black/70"
            >
              <InformationCircleIcon className="size-4" />
            </button>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
};
